/**
 * @file SessionsDaily.js
 * @description Card for the Sessions - Daily dashboard.
 */

import React, { useState, useEffect } from 'react';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
} from 'recharts';
import CustomDashboardTooltip from '../helpers/CustomDashboardTooltip';
import NotEnoughData from './NotEnoughData';
import './SessionsDaily.scss';
import { getText } from '../../i18n';

// Custom renderer for the value to be displayed above the bar graphs
const CustomBarLabel = ({ x, y, width, value }) => {
    return (
        <text
            className="sessions-daily-bar-label"
            x={x + width / 2}
            y={y - 4}
            fill="#2F2B43"
            fontSize={12}
            fontWeight={700}
            textAnchor="middle"
        >
            {value.toLocaleString()}
        </text>
    );
};

const mapApiResponseToSessionsDailyData = dataFromApi => {
    const counts = {
        Sunday: 0,
        Monday: 0,
        Tuesday: 0,
        Wednesday: 0,
        Thursday: 0,
        Friday: 0,
        Saturday: 0,
    };

    dataFromApi.forEach(({ dayOfWeek, count }) => {
        switch (dayOfWeek) {
            case 'Monday':
                counts.Monday += count;
                break;
            case 'Tuesday':
                counts.Tuesday += count;
                break;
            case 'Wednesday':
                counts.Wednesday += count;
                break;
            case 'Thursday':
                counts.Thursday += count;
                break;
            case 'Friday':
                counts.Friday += count;
                break;
            case 'Saturday':
                counts.Saturday += count;
                break;
            case 'Sunday':
                counts.Sunday += count;
                break;
            default:
                break;
        }
    });

    return [
        {
            day: 'M',
            dayOfWeek: 'Monday',
            count: counts.Monday,
            dayOfWeekEnum: 1,
        },
        {
            day: 'T',
            dayOfWeek: 'Tuesday',
            count: counts.Tuesday,
            dayOfWeekEnum: 2,
        },
        {
            day: 'W',
            dayOfWeek: 'Wednesday',
            count: counts.Wednesday,
            dayOfWeekEnum: 3,
        },
        {
            day: 'T',
            dayOfWeek: 'Thursday',
            count: counts.Thursday,
            dayOfWeekEnum: 4,
        },
        {
            day: 'F',
            dayOfWeek: 'Friday',
            count: counts.Friday,
            dayOfWeekEnum: 5,
        },
        {
            day: 'S',
            dayOfWeek: 'Saturday',
            count: counts.Saturday,
            dayOfWeekEnum: 6,
        },
        {
            day: 'S',
            dayOfWeek: 'Sunday',
            count: counts.Sunday,
            dayOfWeekEnum: 0,
        },
    ];
};
const areAllCountsZero = data => {
    if (!Array.isArray(data) || data.length === 0) {
        return true;
    }
    return data.every(item => 'count' in item && item.count === 0);
};

const SessionsDaily = ({ startDate, endDate, sessionsDailyData }) => {
    const [chartData, setChartData] = useState([]);
    const [mostActiveDay, setMostActiveDay] = useState(null);
    const [leastActiveDay, setLeastActiveDay] = useState(null);
    const [averageDailyActiveUsers, setAverageDailyActiveUsers] = useState(0);

    useEffect(() => {
        if (sessionsDailyData?.length > 0) {
            // Set the data for the chart
            const mappedData =
                mapApiResponseToSessionsDailyData(sessionsDailyData);
            setChartData(mappedData);

            // Handle the case if there is not enough data, when displaying the most/least active dates
            const totalActiveUsers = mappedData.reduce(
                (total, day) => total + day.count,
                0,
            );
            if (totalActiveUsers === 0) {
                setMostActiveDay('Not Enough Data');
                setLeastActiveDay('Not Enough Data');
                setAverageDailyActiveUsers(0);
                return;
            }
            // Calculate most active/least active day of week
            const mostActiveDayOfWeek = mappedData.reduce((a, b) =>
                a.count > b.count ? a : b,
            ).dayOfWeek;
            const leastActiveDayOfWeek = mappedData.reduce((a, b) =>
                a.count < b.count ? a : b,
            ).dayOfWeek;
            setMostActiveDay(mostActiveDayOfWeek);
            setLeastActiveDay(leastActiveDayOfWeek);

            // Calculate the daily average active users by week
            const averageUsers = Math.abs(totalActiveUsers / 7).toFixed(0);
            setAverageDailyActiveUsers(averageUsers);
        } else {
            setChartData([]);
            setMostActiveDay('Not Enough Data');
            setLeastActiveDay('Not Enough Data');
            setAverageDailyActiveUsers(0);
        }
    }, [sessionsDailyData, startDate, endDate]);

    if (areAllCountsZero(chartData)) {
        return <NotEnoughData />;
    }

    return (
        <div className="sessions-daily-container">
            <div className="sessions-header">
                <div className="sessions-count">
                    {Number(averageDailyActiveUsers).toLocaleString()}
                </div>
                <div className="sessions-text">{getText('msg_e92c48ff194e')}</div>
            </div>
            <div className="chart-row">
                <ResponsiveContainer width="100%" height={250}>
                    <BarChart
                        data={chartData}
                        margin={{ top: 40, right: 0, left: 0, bottom: 0 }}
                    >
                        {/*This defines the color of the bar gradient for each bar*/}
                        <defs>
                            <linearGradient
                                id="sessionsGradient"
                                x1="0"
                                y1="0"
                                x2="0"
                                y2="1"
                            >
                                <stop
                                    offset="0%"
                                    stopColor="#1890FF"
                                    stopOpacity={1}
                                />
                                <stop
                                    offset="100%"
                                    stopColor="#4318FF"
                                    stopOpacity={0.28}
                                />
                            </linearGradient>
                        </defs>

                        <XAxis
                            dataKey="day"
                            tickLine={false}
                            axisLine={false}
                            tick={({ x, y, payload }) => (
                                <text
                                    x={x}
                                    y={y + 15}
                                    textAnchor="middle"
                                    fill="#B0BBD5"
                                    fontFamily="Inter"
                                    fontSize={12}
                                    fontWeight={700}
                                    letterSpacing="-0.24px"
                                >
                                    {payload.value}
                                </text>
                            )}
                        />

                        <YAxis hide />
                        <Tooltip
                            content={({ active, payload }) => {
                                if (payload && payload.length > 0) {
                                    const { count, dayOfWeek } =
                                        payload[0].payload;
                                    return (
                                        <CustomDashboardTooltip
                                            active={active}
                                            label={dayOfWeek}
                                            value={count}
                                            unit="sessions"
                                        />
                                    );
                                }
                            }}
                            cursor={false}
                        />
                        <Bar
                            dataKey="count"
                            radius={[4, 4, 0, 0]}
                            fill="url(#sessionsGradient)"
                            barSize={20}
                            label={<CustomBarLabel />}
                            minPointSize={1}
                        />
                    </BarChart>
                </ResponsiveContainer>
                <div className="sessions-legend">
                    <div className="legend-item">
                        <span className="dot green" />
                        <div className="legend-text">
                            <div className="legend-label">{getText('msg_cf01766b30bc')}</div>
                            <div className="legend-value">{mostActiveDay}</div>
                        </div>
                    </div>
                    <div className="legend-item">
                        <span className="dot red" />
                        <div className="legend-text">
                            <div className="legend-label">{getText('msg_6810a3148b5f')}</div>
                            <div className="legend-value">{leastActiveDay}</div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SessionsDaily;
